"use client";

import { c as _c } from "react/compiler-runtime";
import { CreateDropPart, ReferencedNft } from "@/entities/IDrop";
import { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import { cicToType } from "@/helpers/Helpers";
import { AnimatePresence, motion } from "framer-motion";
import Link from "next/link";
import { FC, memo, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../auth/Auth";
import UserCICAndLevel, { UserCICAndLevelSize } from "../user/utils/UserCICAndLevel";
import CreateDropStormPart from "./CreateDropStormPart";
interface CreateDropStormPartsProps {
  parts: CreateDropPart[];
  mentionedUsers: ApiDropMentionedUser[];
  referencedNfts: ReferencedNft[];
  onRemovePart: (partIndex: number) => void;
}
const CreateDropStormParts: FC<CreateDropStormPartsProps> = t0 => {
  const $ = _c(41);
  const {
    parts,
    mentionedUsers,
    referencedNfts,
    onRemovePart
  } = t0;
  const {
    connectedProfile
  } = useContext(AuthContext);
  const t1 = connectedProfile?.cic ?? 0;
  let t2;
  if ($[0] !== t1) {
    t2 = cicToType(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const cicType = t2;
  const partIdCounterRef = useRef(0);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = new Map();
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const [partIdsMap, setPartIdsMap] = useState(t3);
  let t4;
  let t5;
  if ($[3] !== parts) {
    t4 = () => {
      setPartIdsMap(prevMap => {
        const newMap = new Map(prevMap);
        let changed = false;
        parts.forEach((part, index) => {
          if (!part.quoted_drop) {
            if (!newMap.has(index)) {
              partIdCounterRef.current = partIdCounterRef.current + 1;
              newMap.set(index, `part-${partIdCounterRef.current}`);
              changed = true;
            }
          }
        });
        const maxIndex = parts.length - 1;
        Array.from(newMap.keys()).forEach(key => {
          if (key > maxIndex) {
            newMap.delete(key);
            changed = true;
          }
        });
        return changed ? newMap : prevMap;
      });
    };
    t5 = [parts];
    $[3] = parts;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  useEffect(t4, t5);
  let t6;
  if ($[6] !== partIdsMap || $[7] !== parts) {
    let t7;
    if ($[9] !== partIdsMap) {
      t7 = (part_0, index_0) => {
        if (part_0.quoted_drop) {
          return `quoted-${part_0.quoted_drop.drop_id}-${part_0.quoted_drop.drop_part_id}`;
        }
        return partIdsMap.get(index_0) ?? `part-fallback-${index_0}`;
      };
      $[9] = partIdsMap;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    t6 = parts.map(t7);
    $[6] = partIdsMap;
    $[7] = parts;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const partKeys = t6;
  let t7;
  if ($[11] !== connectedProfile) {
    t7 = <div className="tw-h-10 tw-w-10 tw-bg-iron-900 tw-relative tw-flex-shrink-0 tw-rounded-lg">{connectedProfile?.pfp ? <img src={connectedProfile.pfp} alt={connectedProfile.handle ?? "user"} className="tw-h-full tw-w-full tw-object-cover tw-rounded-lg" /> : <div className="tw-h-full tw-w-full tw-rounded-lg tw-bg-iron-900" />}</div>;
    $[11] = connectedProfile;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = connectedProfile?.level ?? 0;
  let t9;
  if ($[13] !== cicType || $[14] !== t8) {
    t9 = <UserCICAndLevel level={t8} cicType={cicType} size={UserCICAndLevelSize.SMALL} />;
    $[13] = cicType;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  const t10 = `/${connectedProfile?.handle}`;
  const t11 = connectedProfile?.handle ?? "user";
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <p className="tw-text-md tw-mb-0 tw-leading-none tw-font-semibold"><Link href={t10} className="tw-no-underline tw-text-iron-200 hover:tw-text-iron-500 tw-transition tw-duration-300 tw-ease-out">{t11}</Link></p>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== t12 || $[20] !== t9) {
    t13 = <div className="tw-flex tw-items-center tw-gap-x-2"><div className="tw-flex tw-items-center tw-gap-x-2">{t9}{t12}</div></div>;
    $[19] = t12;
    $[20] = t9;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== mentionedUsers || $[23] !== onRemovePart || $[24] !== partKeys || $[25] !== parts || $[26] !== referencedNfts) {
    let t15;
    if ($[28] !== mentionedUsers || $[29] !== onRemovePart || $[30] !== partKeys || $[31] !== referencedNfts) {
      t15 = (part_1, partIndex) => <motion.div key={partKeys[partIndex]} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.3
      }}><CreateDropStormPart partIndex={partIndex} part={part_1} mentionedUsers={mentionedUsers} referencedNfts={referencedNfts} onRemovePart={onRemovePart} /></motion.div>;
      $[28] = mentionedUsers;
      $[29] = onRemovePart;
      $[30] = partKeys;
      $[31] = referencedNfts;
      $[32] = t15;
    } else {
      t15 = $[32];
    }
    t14 = parts.map(t15);
    $[22] = mentionedUsers;
    $[23] = onRemovePart;
    $[24] = partKeys;
    $[25] = parts;
    $[26] = referencedNfts;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[33] !== t14) {
    t15 = <div className="tw-mt-4 tw-space-y-4"><AnimatePresence mode="popLayout">{t14}</AnimatePresence></div>;
    $[33] = t14;
    $[34] = t15;
  } else {
    t15 = $[34];
  }
  let t16;
  if ($[35] !== t13 || $[36] !== t15) {
    t16 = <div className="tw-flex tw-flex-col tw-w-full">{t13}{t15}</div>;
    $[35] = t13;
    $[36] = t15;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t16 || $[39] !== t7) {
    t17 = <div className="tw-space-y-4 tw-pb-3"><div className="tw-bg-transparent tw-relative tw-group tw-w-full tw-flex tw-flex-col tw-py-2 tw-transition-colors tw-duration-300"><div className="tw-flex tw-gap-x-3">{t7}{t16}</div></div></div>;
    $[38] = t16;
    $[39] = t7;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  return t17;
};
export default memo(CreateDropStormParts);